import React, { useMemo } from "react";
import { X } from "@phosphor-icons/react";
import { formatMoney } from "@/lib/format";

const SCHEME_LABELS = { eis: "EIS Eligible", seis: "SEIS Eligible", both: "EIS + SEIS", none: "Neither" };
const MULTI_KEYS = ["industry", "stage", "revenue_status", "region", "country", "priority"];
const KEY_LABELS = {
  industry: "Industry",
  stage: "Stage",
  revenue_status: "Revenue",
  region: "Region",
  country: "Country",
  priority: "Priority",
};

const DEFAULT_MIN = 50000;
const DEFAULT_MAX = 20000000;

export default function ActiveFilterChips({ filters, setFilters, onReset }) {
  const chips = useMemo(() => {
    const list = [];
    MULTI_KEYS.forEach((k) => {
      (filters[k] || []).forEach((v) => {
        list.push({
          id: `${k}-${v}`,
          label: `${KEY_LABELS[k]}: ${v}`,
          onRemove: () =>
            setFilters((f) => ({ ...f, [k]: (f[k] || []).filter((x) => x !== v) })),
        });
      });
    });
    if (filters.scheme) {
      list.push({
        id: `scheme-${filters.scheme}`,
        label: `Scheme: ${SCHEME_LABELS[filters.scheme] || filters.scheme}`,
        onRemove: () => setFilters((f) => ({ ...f, scheme: null })),
      });
    }
    const raiseTouched =
      (filters.min_raise ?? DEFAULT_MIN) > DEFAULT_MIN ||
      (filters.max_raise ?? DEFAULT_MAX) < DEFAULT_MAX;
    if (raiseTouched) {
      list.push({
        id: "ticket-size",
        label: `Ticket: ${formatMoney(filters.min_raise)} – ${formatMoney(filters.max_raise)}`,
        onRemove: () =>
          setFilters((f) => ({ ...f, min_raise: DEFAULT_MIN, max_raise: DEFAULT_MAX })),
      });
    }
    return list;
  }, [filters, setFilters]);

  if (chips.length === 0) return null;

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-2"
      data-testid="active-filter-chips"
    >
      <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mr-1">
        Active
      </span>
      {chips.map((chip) => (
        <button
          key={chip.id}
          onClick={chip.onRemove}
          data-testid={`chip-${chip.id.toLowerCase().replace(/\s+/g, "-")}`}
          className="group inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-xs font-medium hover:bg-emerald-100 dark:hover:bg-emerald-950/70 transition-colors"
        >
          <span>{chip.label}</span>
          <X size={12} weight="bold" className="opacity-60 group-hover:opacity-100" />
        </button>
      ))}
      {chips.length >= 2 && (
        <button
          onClick={onReset}
          data-testid="chip-clear-all"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-muted-foreground hover:text-foreground underline underline-offset-4 decoration-dotted"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
